import React, { useState } from 'react'
import { Button, Input, Option, Select, Spinner, Typography, Switch } from '@material-tailwind/react'
import { useTranslation } from 'react-i18next'
import { colors } from '@material-tailwind/react/types/generic'
import { useFormData } from '../hooks/form'
import { validateInputNumber } from '../utils/helpers/validate.helpers'
import { useGeneralToast } from '../hooks/toast'
import { createExpAccessCode } from '../utils/helpers/supabase.helpers'
import { useDarkMode } from '../contexts/DarkModeContext'

interface ExperienceOption {
  id: string
  name: string
}

interface CreateAccessCodeFormProps {
  experienceList: ExperienceOption[]
  refetch: () => void
  closeDialog?: () => void
}

const CreateAccessCodeForm: React.FC<CreateAccessCodeFormProps> = ({
  experienceList,
  refetch,
  closeDialog,
}) => {
  const { t } = useTranslation()
  const { darkMode } = useDarkMode()
  const toast = useGeneralToast()
  const [generating, setGenerating] = useState(false)

  const { formData, handleDataChange } = useFormData({
    anonymous_access: false,
    experienceId: '',
    expiresAt: '',
    accessExpiresAfter: '',
    code: '',
  })

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    e.stopPropagation()

    setGenerating(true)

    if (!formData.experienceId) {
      toast.error(t('messages.chooseExperienceError'))
    }
    const { error } = await createExpAccessCode(formData)

    if (error) {
      toast.error((error as { message?: string })?.message ?? JSON.stringify(error))
      setGenerating(false)
      return
    }

    refetch()
    setGenerating(false)
    if (closeDialog) closeDialog()
    toast.success(t('messages.codeGeneratedSuccess'))
  }

  return (
    <form className="flex flex-col gap-4 px-2" onSubmit={onSubmit} data-testid="access-code-form">
      <div className="flex flex-col">
        <Select
          label={t('forms.chooseExperience')}
          value={formData.experienceId ?? ''}
          onChange={(val) => handleDataChange('experienceId', val || '')}
          menuProps={{ className: 'dark:bg-primary' }}
          data-testid="access-code-experience-select"
          {...(darkMode && { color: 'blue-gray' })}
          labelProps={{
            className:
              'text-gray-600 dark:!text-gray-200 peer-focus:!text-gray-800 dark:peer-focus:!text-gray-100',
          }}
        >
          {experienceList.map((exp) => (
            <Option key={exp.id} value={exp.id} data-testid={`access-code-experience-option-${exp.id}`}>
              {exp.name}
            </Option>
          ))}
        </Select>
        <Typography variant="small">{t('forms.targetExperience')}</Typography>
      </div>

      <div className="flex flex-col">
        <Input
          value={formData.expiresAt ?? ''}
          onChange={(e) => handleDataChange('expiresAt', e.currentTarget.value)}
          type="datetime-local"
          label={t('forms.codeExpiresAt')}
          data-testid="access-code-expires-at-input"
          {...(darkMode && { color: 'white' })}
        />
        <Typography variant="small">{t('forms.codeExpiresAtHelp')}</Typography>
      </div>

      <div className="flex flex-col">
        <Input
          value={formData.accessExpiresAfter ?? ''}
          onChange={(e) =>
            handleDataChange('accessExpiresAfter', String(validateInputNumber(e.currentTarget.value ?? '')))
          }
          type="number"
          label={t('forms.accessExpiresAfter')}
          {...(darkMode && { color: 'white' })}
        />
        <Typography variant="small">{t('forms.accessExpiresAfterHelp')}</Typography>
      </div>

      <div className="flex flex-col">
        <Input
          value={formData.code ?? ''}
          onChange={(e) => handleDataChange('code', e.currentTarget.value)}
          type="text"
          label={t('forms.code')}
          data-testid="access-code-input"
          {...(darkMode && { color: 'white' })}
        />
        <Typography variant="small">{t('forms.codeHelp')}</Typography>
      </div>

      <div className="flex flex-col">
        <Switch
          id="anonymous_access"
          color={(darkMode ? 'accent' : 'primary') as colors}
          label={t('forms.guestAccessCode')}
          checked={Boolean(formData.anonymous_access)}
          onChange={(e) => handleDataChange('anonymous_access', e.currentTarget.checked)}
        />
        <Typography variant="small">{t('forms.guestAccessCodeHelp')}</Typography>
      </div>

      <Button
        type="submit"
        disabled={generating || !formData.experienceId}
        data-testid="generate-access-code-btn"
      >
        {generating ? <Spinner className="mx-auto" /> : t('forms.generateCode')}
      </Button>
    </form>
  )
}

export default CreateAccessCodeForm
